"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listReturnUrl = exports.saveListReturnUrl = exports.RESTORE_SCROLL_STATE = void 0;
exports.useUrlPage = useUrlPage;
exports.useSyncUrlPage = useSyncUrlPage;
exports.useUrlSearch = useUrlSearch;
const react_1 = require("react");
const LIST_URL_PREFIX = 'list-url:';
/** 麵包屑連回列表時帶的 state：ScrollManager 看到它就還原列表離開時的捲動位置（同按瀏覽器上一頁） */
exports.RESTORE_SCROLL_STATE = { restoreScroll: true };
/**
 * 改網址參數：以瀏覽器目前的網址為準（render 時拿到的 searchParams 可能已經過期，
 * 例：同一輪裡頁碼、搜尋先後各改一次，用舊的會把另一個蓋回去）；一律 replace
 */
const replaceParam = (setSearchParams, name, value) => {
    const next = new URLSearchParams(window.location.search);
    if ((next.get(name) ?? null) === value)
        return;
    if (value)
        next.set(name, value);
    else
        next.delete(name);
    setSearchParams(() => next, { replace: true });
};
const parsePage = (raw) => {
    const page = Number(raw);
    return Number.isInteger(page) && page > 1 ? page : 1;
};
/** 進頁面時要載入的頁碼（網址的 ?page=）；同一個列表內換頁不會變，換到別的路徑（例：已封存列表）才重新讀 */
function useUrlPage({ pathname, searchParams }) {
    const initial = (0, react_1.useRef)(null);
    if (initial.current?.pathname !== pathname)
        initial.current = { pathname, page: parsePage(searchParams.get('page')) };
    return initial.current.page;
}
/**
 * 列表實際顯示的頁碼寫回網址（換頁、搜尋後回第 1 頁、批次刪除後跳頁都會跟著變）
 * - replace：換頁不多一筆上一頁紀錄（點進詳情頁才是新的一筆）
 * - 網址的頁碼超過總頁數（返回前資料被刪了）時跳到最後一頁
 * - 網址被外部改掉（已經在列表頁又點側欄同一項）時跟著網址換頁
 * - 記下這個列表最後的網址，詳情頁的麵包屑用 listReturnUrl() 回到同一頁
 */
function useSyncUrlPage({ pathname, navigationType, searchParams, setSearchParams }, pagination, goToPage) {
    const currentPage = pagination?.currentPage;
    const totalPages = pagination?.totalPages;
    const urlPageParam = searchParams.get('page');
    const search = searchParams.toString();
    (0, react_1.useEffect)(() => {
        if (!currentPage || totalPages === undefined)
            return;
        const lastPage = Math.max(totalPages, 1); // 沒有資料時總頁數是 0
        if (currentPage > lastPage) {
            goToPage(lastPage);
            return;
        }
        replaceParam(setSearchParams, 'page', currentPage > 1 ? String(currentPage) : null);
    }, [currentPage, totalPages]); // eslint-disable-line react-hooks/exhaustive-deps
    // 網址被外部改掉（同一頁點側欄：搜尋、頁碼一起清掉）：跟著網址換頁；換路徑時由頁面依 useUrlPage 重新載入，這裡不管
    const seen = (0, react_1.useRef)({ pathname, search });
    (0, react_1.useEffect)(() => {
        const external = seen.current.pathname === pathname && seen.current.search !== search && navigationType !== 'REPLACE';
        seen.current = { pathname, search };
        const page = parsePage(urlPageParam);
        if (external && currentPage && currentPage !== page)
            goToPage(page);
    }, [pathname, search]); // eslint-disable-line react-hooks/exhaustive-deps
    (0, react_1.useEffect)(() => (0, exports.saveListReturnUrl)(pathname, search ? `${pathname}?${search}` : pathname), [pathname, search]);
}
/** 記下「回到這個列表」要去的網址（useSyncUrlPage 會自動記；另一種檢視，例：CRM 商機看板，自己呼叫） */
const saveListReturnUrl = (listPath, url) => {
    try {
        sessionStorage.setItem(LIST_URL_PREFIX + listPath, url);
    }
    catch { /* sessionStorage 不可用時麵包屑回第 1 頁 */ }
};
exports.saveListReturnUrl = saveListReturnUrl;
/**
 * 列表搜尋文字存在網址 ?q=：從詳情頁返回、重新整理時保留；從側欄進來沒有 ?q= 就是空的（主流做法，例：GitHub、Gmail）
 * - 輸入框綁這裡回傳的 state，再同步到網址（直接綁網址的話，中文選字時游標可能亂跳）
 * - 網址被外部改掉（點側欄、上一頁、未封存 ↔ 已封存）時跟著網址；自己同步的（replace）不算：
 *   打字很快時網址會落後幾個字，不能拿來蓋掉輸入框
 */
function useUrlSearch({ navigationType, searchParams, setSearchParams }) {
    const urlQ = searchParams.get('q') ?? '';
    const [term, setTerm] = (0, react_1.useState)(urlQ);
    const [seenUrlQ, setSeenUrlQ] = (0, react_1.useState)(urlQ);
    if (seenUrlQ !== urlQ) {
        setSeenUrlQ(urlQ);
        if (navigationType !== 'REPLACE')
            setTerm(urlQ);
    }
    const q = term.trim();
    (0, react_1.useEffect)(() => replaceParam(setSearchParams, 'q', q || null), [q]); // eslint-disable-line react-hooks/exhaustive-deps
    return [term, setTerm];
}
/** 詳情頁麵包屑連回列表：這個分頁最後看的那一頁（沒看過就是列表第 1 頁） */
const listReturnUrl = (listPath) => {
    try {
        return sessionStorage.getItem(LIST_URL_PREFIX + listPath) ?? listPath;
    }
    catch {
        return listPath;
    }
};
exports.listReturnUrl = listReturnUrl;
